import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, ShoppingCart } from 'lucide-react';
import type { OpenOrderItem } from '@uniticket/shared';
import { isOpenOrderStatus, unitShowsOpenOrderCart } from '@uniticket/shared';
import { cn } from '@/lib/utils';
import type { OpenOrdersState } from '@/hooks/useAddressValidation';

// N42 (08-10) — le caddie « ordre en cours » de la fenêtre de validation : un client a demandé le raccordement de ce
// logement, et l'ordre n'est pas terminé. Il se pose en fin de ligne du tableau des logements (la maquette du user)
// et, pour un SDU qui n'a pas de tableau, dans l'en-tête du panneau 1.
//
// La règle (`unitShowsOpenOrderCart`, partagée avec le serveur) : au moins un ordre en cours ET un logement qui
// n'est PAS Homes Connect. Le statut reçu est celui que l'écran affiche déjà pour ce logement — le caddie ne peut
// donc pas contredire la colonne Statut.
//
// Pas de clic propre : dans le tableau, la LIGNE est déjà le bouton (elle ouvre la fiche du logement). Le détail
// tient dans l'infobulle native, comme pour le code de livraison.

/** Les ordres en cours d'un logement, ou `null` tant que la lecture n'a pas répondu (ou a échoué). */
export function openOrdersFor(state: OpenOrdersState, jvid: number): OpenOrderItem[] | null {
  if (state.status !== 'ready' || !state.byJvid) return null;
  return (state.byJvid[String(jvid)] ?? []).filter((o) => isOpenOrderStatus(o.orderStatus));
}

export function OpenOrderCart({
  state, jvid, unitStatus, className,
}: { state: OpenOrdersState; jvid: number; unitStatus: string | null | undefined; className?: string }) {
  const { t } = useTranslation(['tickets']);
  const orders = openOrdersFor(state, jvid);
  if (!orders || !unitShowsOpenOrderCart(unitStatus, orders)) return null;

  // Libellés traduits, repli sur la valeur brute du DMP : un statut ou un type que personne n'a encore vu doit
  // rester lisible, pas devenir une clé.
  const label = (group: 'status' | 'type', raw: string | null) => (raw
    ? t(`tickets:validation.openOrders.${group}.${raw}`, { defaultValue: raw })
    : t('tickets:validation.openOrders.unknown'));
  const title = [
    t('tickets:validation.openOrders.title', { count: orders.length }),
    ...orders.map((o) => t('tickets:validation.openOrders.line', {
      type: label('type', o.orderType),
      status: label('status', o.orderStatus),
      operator: o.operatorName ?? '—',
      reference: o.externalReference ?? '—',
      // Même écriture que le volet « Commandes » (jour, mois en lettres) : sans équivoque quelle que soit la langue.
      date: o.createdAt
        ? new Date(o.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : '—',
    })),
  ].join('\n');

  return (
    <span role="img" title={title} aria-label={title} className={cn('inline-flex align-middle text-red-600', className)}>
      <ShoppingCart className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

/**
 * Ce que la lecture a à DIRE quand elle n'a pas (encore) répondu. Pendant la lecture : une mention discrète.
 * En cas d'échec : une note ambre et « Réessayer » — jamais un silence, parce qu'un caddie absent se lirait
 * « aucun ordre, je peux toucher ce logement ». Rien quand la lecture a répondu.
 */
export function OpenOrdersNotice({ state, className }: { state: OpenOrdersState; className?: string }) {
  const { t } = useTranslation(['tickets']);
  if (state.status === 'loading') {
    return (
      <p className={cn('flex items-center gap-1.5 text-[11px] text-slate-400', className)}>
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
        {t('tickets:validation.openOrders.loading')}
      </p>
    );
  }
  if (state.status !== 'error') return null;
  return (
    <p role="alert" className={cn('flex flex-wrap items-center gap-1.5 text-[11px] font-medium text-amber-700', className)}>
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {t('tickets:validation.openOrders.error')}
      <button
        type="button"
        onClick={state.retry}
        className="underline decoration-dotted underline-offset-2 hover:text-amber-900"
      >
        {t('tickets:validation.openOrders.retry')}
      </button>
    </p>
  );
}
